import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import { CheckCircle2, ArrowRight, Briefcase, Eye, Users } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function LinkedInOptimizerPage() {
  return (
    <>
      <Helmet>
        <title>LinkedIn Optimizer - Get Noticed by Recruiters | LoopBaan.ai</title>
        <meta name="description" content="Optimize your LinkedIn profile with AI. Get found by recruiters and stand out in search results." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <MegaMenuNav />

        <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-primary/20 text-primary border-primary/30">VISIBILITY</Badge>
              <h1 className="mb-6 text-5xl md:text-7xl font-bold leading-tight">
                Get Found By{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Recruiters
                </span>
              </h1>
              <p className="mb-8 text-xl text-muted-foreground max-w-2xl mx-auto">
                AI optimizes your LinkedIn profile to rank higher in recruiter searches. Get noticed, get messaged, get hired.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/signup">
                  <Button size="lg" className="gap-2">
                    Optimize Profile <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
              {[
                {
                  icon: Eye,
                  title: "SEO Optimization",
                  description: "AI adds the right keywords so recruiters find you when searching for candidates with your skills."
                },
                {
                  icon: Users,
                  title: "Profile Enhancement",
                  description: "Improve your headline, about section, and experience descriptions to make a strong impression."
                },
                {
                  icon: Briefcase,
                  title: "Industry Best Practices",
                  description: "Follow proven strategies that top performers use to attract recruiters and opportunities."
                }
              ].map((feature, i) => (
                <Card key={i} className="p-6 hover:shadow-lg transition-all">
                  <feature.icon className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">What Gets Optimized</h2>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  "Headline with target keywords",
                  "About section storytelling",
                  "Experience bullet points",
                  "Skills section completeness",
                  "Recommendations strategy",
                  "Featured section content",
                  "Profile photo tips",
                  "Banner image guidance"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <span className="text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold mb-8">Why It Matters</h2>
              <div className="grid gap-8 md:grid-cols-3">
                <div>
                  <div className="text-5xl font-bold text-primary mb-2">87%</div>
                  <div className="text-muted-foreground">of Recruiters Use LinkedIn</div>
                </div>
                <div>
                  <div className="text-5xl font-bold text-primary mb-2">3x</div>
                  <div className="text-muted-foreground">More Profile Views</div>
                </div>
                <div>
                  <div className="text-5xl font-bold text-primary mb-2">40%</div>
                  <div className="text-muted-foreground">More InMail Messages</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Let Opportunities Come to You
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Stop applying. Start getting recruited. Optimize your LinkedIn today.
            </p>
            <Link to="/signup">
              <Button size="lg" className="gap-2">
                Optimize Now <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
